import type { PatternSourceFields } from "./pattern-types";
import type { Platform } from "./prototype-types";

export interface Component extends PatternSourceFields {
  /** Stable id for pattern prompts; derived from path when omitted. */
  id?: string;
  name: string;
  path: string;
  description?: string;
  owner?: string;
  thumbnail?: string;
  /** Target platform; unset is treated as `web`. */
  platform?: Platform;
}

export const components: Component[] = [
  {
    name: "Global nav",
    path: "/components/global-nav",
    description: "Navigation bar with search, categories, and user actions",
    owner: "Ryan",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/49c4b9d82cf8fcce4337da2fc124b2b964a4f269d5301b782d376be20393287f.png",
    patternType: "component",
    variants: [
      {
        id: "standard",
        name: "Standard",
        description: "Logged-in nav with search, categories, and cart count",
        previewPath: "/components/global-nav",
        promptInstructions:
          "Use RetailerGlobalNavLoggedIn with default props (see Standard variant on the showcase page).",
      },
      {
        id: "focused",
        name: "Focused",
        description: "Focused header treatment with reduced chrome",
        previewPath: "/components/global-nav",
        promptInstructions:
          "Use RetailerGlobalNavLoggedIn with focused={true} (Focused variant on the showcase page).",
      },
      {
        id: "focused-2",
        name: "Focused 2",
        description: "Alternate focused header layout",
        previewPath: "/components/global-nav",
        promptInstructions:
          "Use RetailerGlobalNavLoggedIn with focused2={true} (Focused variant 2 on the showcase page).",
      },
    ],
  },
  {
    name: "Footer",
    path: "/components/footer",
    description: "Site footer with links and app download badges",
    owner: "Ryan",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/4de3043f6c862c061010ea51bc5534f35dd796b616d550e4c070e4857d76d3e1.png",
  },
  {
    name: "Brand info",
    path: "/components/brand-info",
    description: "Brand information display with rating and badges",
    owner: "Ryan",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/ea9310880bececd246727f4785ae442951b60e176c5e2184e64c5e0df328baa2.png",
  },
  {
    name: "Brand cart toggle",
    path: "/components/brand-cart-toggle",
    description: "Brand avatar with cart icon button",
    owner: "Ryan",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/d234c0578908a20859c9584e041fd769771b9ae3f76aeb1572eb746bd4ee6404.png",
  },
  {
    name: "Brand tile",
    path: "/components/brand-tile",
    description: "Brand display tile with image, rating, and shipping info",
    owner: "Ryan",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/a72661f6fef807553adf24a93a6f061df105d83c37236bd00953f34f27b9a171.png",
  },
  {
    name: "Carousel",
    path: "/components/carousel",
    description: "Horizontal carousel component with variants",
    owner: "Ryan",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/cbbcf0edad40de6d9d104da348800037de83e54bf3cac9dfabf1b73739b9a6cf.png",
  },
  {
    name: "Carousel header",
    path: "/components/carousel-header",
    description: "Header component for carousels with title and shop all link",
    owner: "Ryan",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/e7441b557a7a795f70994cdbf5a6fd74c0ee63d7efd5d9e2e380516d62c85080.png",
  },
  {
    name: "Basic container",
    path: "/components/basic-container",
    description: "Standard container with border and padding",
    owner: "Ryan",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/cd3393fa7a8979eaead371cb4a35f2fb4b12db5320ab856c7438e7169496c7ba.png",
  },
  {
    name: "Cart tile",
    path: "/components/cart-tile",
    description: "Tile displaying brand info, product images, and item count",
    owner: "Ryan",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/cd814f0378ba620764b8902544aefefc5f773c0527f3ee3379c504ac70e49289.png",
  },
  {
    name: "Post-order summary-01",
    path: "/components/post-order-summary-01",
    description:
      "Order summary card with brand, shipping, payment, and savings info",
    owner: "Ryan",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/0505259eb351ca22abee456637330883e1721b4a6e180bd4edfedda972c2ea92.png",
  },
  {
    name: "Product tile",
    path: "/components/product-tile",
    description:
      "Product display tile with image, price, brand info, and quick add button",
    owner: "Ryan",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/7aebfb5394c7640cb29cb617d1fb1b92ade12b88299435bc0bdc98d39ab74221.png",
  },
  {
    name: "Pill button",
    path: "/components/pill-button",
    description: "Filter/toggle button styled as a pill with pressed state",
    owner: "Ryan",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/0971a776ac610caf26a4733e08bf84e4c78cfa0bca2602be0537b030b7be6770.png",
  },
  {
    name: "Variant Switcher",
    path: "/components/variant-switcher",
    description: "Floating concept/variant selector for prototype demos",
    owner: "John",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/6cba55a3f720282175bbcead0dc300a31651a30baf94831b7e0f12b45c5c2d96.png",
  },
  {
    name: "Compare products",
    path: "/components/compare-products",
    description:
      "PDP compare section with header, retailer subtitle, and four product cards",
    owner: "Christopher",
  },
  {
    name: "Review Panel",
    path: "/components/review-panel",
    description:
      "DPP engineering review controls with concept switching and annotation toggle",
    owner: "John",
    thumbnail:
      "https://cdn.prerelease.faire.dev/fastly/0e3348d776d3443471ce1aa45145c7963daaab5d7a21c03026fed132528cc2d2.png",
  },
  {
    id: "compare-products",
    name: "Compare products",
    path: "/components/compare-products",
    description:
      "AI-curated product comparison section from the retailer PDP: an anchor 'Current' card plus aspect cards (lowest order minimum, shortest lead time, highest ratings) with rationale and 'Tell me more' CTAs.",
    owner: "John Intrater",
    patternType: "component",
    thumbnail: "https://cdn.prerelease.faire.dev/fastly/b38a1e78a3c52a247def8eb47be1ae7fd96ec9dd2e2db17ac77f49fab50aab26.png",
    productionReference: "Retailer PDP — Compare products section",
    variants: [
      {
        id: "compare-products",
        name: "Compare products",
        description:
          "AI-curated product comparison section from the retailer PDP: an anchor 'Current' card plus aspect cards (lowest order minimum, shortest lead time, highest ratings) with rationale and 'Tell me more' CTAs.",
        previewPath: "/components/compare-products",
      },
    ],
  },
  {
    id: "retailer-assistant-panel",
    name: "Retailer Assistant panel",
    path: "/components/retailer-assistant-panel",
    description:
      "The Compass 'Your Assistant' side panel: header, AI disclaimer, product context card, chat thread with a research collapsible, cited markdown answer, thumbs feedback, follow-up suggestion pills, and a composer.",
    owner: "John Intrater",
    patternType: "component",
    thumbnail: "https://cdn.prerelease.faire.dev/fastly/045326ba5bee09b18a3cdc853c00e80bd9b17900b97003e19c3415bacc16e77f.png",
    productionReference: "Retailer PDP — Compass assistant side panel",
    variants: [
      {
        id: "retailer-assistant-panel",
        name: "Retailer Assistant panel",
        description:
          "The Compass 'Your Assistant' side panel: header, AI disclaimer, product context card, chat thread with a research collapsible, cited markdown answer, thumbs feedback, follow-up suggestion pills, and a composer.",
        previewPath: "/components/retailer-assistant-panel",
      },
    ],
  },
  {
    id: "retailer-top-nav",
    name: "Retailer top nav",
    path: "/components/retailer-top-nav",
    description:
      "Faire marketplace global top nav: logo, All categories, search, notification/account/cart, and the category row. Sticky to the top.",
    owner: "John Intrater",
    patternType: "component",
    thumbnail: "https://cdn.prerelease.faire.dev/fastly/07c8c411f8eddb6a69818004c32b14e5d8dc7da5795a558ea36ca5a6a8bd9b0f.png",
    productionReference: "Retailer global navigation",
    variants: [
      {
        id: "retailer-top-nav",
        name: "Retailer top nav",
        description:
          "Faire marketplace global top nav: logo, All categories, search, notification/account/cart, and the category row. Sticky to the top.",
        previewPath: "/components/retailer-top-nav",
      },
    ],
  },
  {
    id: "brand-storefront-header",
    name: "Brand storefront header",
    path: "/components/brand-storefront-header",
    thumbnail: "https://cdn.prerelease.faire.dev/fastly/4a896bcf99fe11e59cf29e23354b91043b217a002a89bfc3b151c8c564215e7f.png",
    description:
      "Brand storefront header: full-bleed cover, overlapping avatar, name, Top Shop badge, rating · location · minimum, ship-by estimate, and Follow / Message / more-actions controls.",
    owner: "John Intrater",
    patternType: "component",
    productionReference: "https://www.faire.com/brand/b_tthhaz7z5x",
  },
  {
    id: "marketplace-product-tile",
    name: "Marketplace product tile",
    path: "/components/marketplace-product-tile",
    thumbnail: "https://cdn.prerelease.faire.dev/fastly/b8d3b67dd5965c307b752a20c33d4ca14c80533c1e02c8edf3f1853346aa91ef.png",
    description:
      "Catalog product tile used in brand-storefront and search grids: square image, favorite, round quick-add, overlay badge, price + MSRP, name, 'Case of X', swatches, and preorder/scarcity states.",
    owner: "John Intrater",
    patternType: "component",
    productionReference: "https://www.faire.com/brand/b_tthhaz7z5x",
  },
  {
    id: "marketplace-brand-tile",
    name: "Marketplace brand tile",
    path: "/components/marketplace-brand-tile",
    thumbnail: "https://cdn.prerelease.faire.dev/fastly/2900bbb6b5c9ab204d96b3726474cecb2cf96c49ff03275ae8c15ba76cfc2c56.png",
    description:
      "Brand recommendation tile ('Brands you might like'): cover image, Top Shop badge, overlapping avatar, name, rating · minimum, and Insider / sale perk lines.",
    owner: "John Intrater",
    patternType: "component",
    productionReference: "https://www.faire.com/brand/b_tthhaz7z5x",
  },
  {
    id: "brand-reviews",
    name: "Brand ratings & reviews",
    path: "/components/brand-reviews",
    thumbnail: "https://cdn.prerelease.faire.dev/fastly/62c4ab02ddba836774f78dce12f4202542f2903576db0b8b6a0d7364830616d4.png",
    description:
      "Brand 'Ratings and reviews' section: score summary with rating distribution and granular metrics, plus the review list with purchased products and pagination.",
    owner: "John Intrater",
    patternType: "component",
    productionReference: "https://www.faire.com/brand/b_tthhaz7z5x",
  },
  {
    id: "brand-side-cart",
    name: "Brand side cart",
    path: "/components/brand-side-cart",
    thumbnail: "https://cdn.prerelease.faire.dev/fastly/406c5933dad4f70095cdd805c8cf040af0a427e112eb670129a36c7bd42f7445.png",
    description:
      "Retailer-facing brand side cart: brand name, order-minimum progress, editable line items with quantity + remove, a 'Complete your cart' recommendation list, and a totals footer with View cart / Checkout (disabled until the order minimum is reached).",
    owner: "John Intrater",
    patternType: "component",
    productionReference: "https://www.faire.com/brand/b_tthhaz7z5x",
  },
  {
    id: "brand-filter-rail",
    name: "Brand filter rail",
    path: "/components/brand-filter-rail",
    thumbnail: "https://cdn.prerelease.faire.dev/fastly/c37505f2b11e14fcc014b50126d226506df174440e35d59ee242817fa44146b7.png",
    description:
      "Brand storefront category filters: the collapsible left rail (category tree + searchable product types + price/ship/GTIN/category facets), the All-filters/Hide-filters toggle with category pills and sort control, and the compact small-screen filter modal.",
    owner: "John Intrater",
    patternType: "component",
    productionReference: "https://www.faire.com/brand/b_tthhaz7z5x",
  },
];

export const getTitleForComponentPath = (pathname: string): string => {
  const component = components.find((c) => c.path === pathname);
  return component ? component.name : "Component";
};
